import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { MerChantModel } from '../../../../models/merChantModel';
import { SelectChangeEvent } from '@mui/material';
import { ProductMerchantModel } from '../../../../models/productMerchant';
import { handleGetProductByItemCode } from '../../../../utils/getProductByItemCodeFunction';
import moment from 'moment';
import { PromotionEnumType } from '../../../../enums/promotionEnum';
import { createPromotionService } from '../../../../services/promotion';
import { getIdFormSelectBranchData } from '../../../../utils/getIdFormSelectBranchData';
import { TagPromotionModel } from '../../../../models/tagPromotion.model';
import { PromotionModel } from '../../../../models/promotion';
import { EN_ACCEPT_LANGUAGE, LA_ACCEPT_LANGUAGE } from '../../../../constants/acceptLanguageApi';
import { useTranslation } from 'react-i18next';
import { getDateTimeFromRef } from '../../../../utils/dateTimeFormat';
import { PromotionTypeEnumPromotion } from '../../../../enums/discountEnum';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

interface Props {
  branchData: MerChantModel[];
  promotionType: PromotionEnumType;
  onFetchData: Function;
  onClose: VoidFunction;
  tagPromotionData: TagPromotionModel[];
  selectedData: PromotionModel;
}

const useBuyXGetCSTPromotionController = ({ branchData, promotionType, onFetchData, onClose, tagPromotionData, selectedData }: Props) => {
  const { t, i18n } = useTranslation('global');
  const [isFlashSale, setIsFlashSale] = useState<boolean>(false);

  const [getDataProduct, setGetDataProduct] = useState<ProductMerchantModel[]>([]);
  const [dataProductBySelect, setDataProductBySelect] = useState<ProductMerchantModel[]>([]);
  const [openConfirmForm, setOpenConfirmForm] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorMessageSearchProduct, setErrorMessageSearchProduct] = useState<string>('');

  const [selectedStartDateHour, setSelectedStartDateHour] = useState<string>('');
  const [selectedEndDateHour, setSelectedEndDateHour] = useState<string>('');
  const [selectedFlashSaleStartDateHour, setSelectedFlashSaleStartDateHour] = useState<string>('');
  const [selectedFlashSaleEndDateHour, setSelectedFlashSaleEndDateHour] = useState<string>('');
  const [selectedProductValue, setSelectedProductValue] = useState<number>(0);
  const [selectBranchDataValue, setSelectBranchDataValue] = useState<string[]>([]);
  const [selectedPromotionPerDay, setSelectedPromotionPerDay] = useState<PromotionTypeEnumPromotion>(PromotionTypeEnumPromotion.PROMOTION);
  const [selectedTagPromotion, setSelectedTagPromotion] = useState<string>('');

  const itemCodeX = useRef<HTMLInputElement>(null!);
  const promotionName = useRef<HTMLInputElement>(null!);
  const description = useRef<HTMLInputElement>(null!);
  const startDate = useRef<HTMLInputElement>(null!);
  const endDate = useRef<HTMLInputElement>(null!);
  const flashSaleStartDate = useRef<HTMLInputElement>(null!);
  const flashSaleEndDate = useRef<HTMLInputElement>(null!);
  const quantityBuyProduct = useRef<HTMLInputElement>(null!);
  const getPoint = useRef<HTMLInputElement>(null!);
  const limitUse = useRef<HTMLInputElement>(null!);
  const limitUseOfCustomer = useRef<HTMLInputElement>(null!);
  const limitProPerDay = useRef<HTMLInputElement>(null!);

  const getBranchId = getIdFormSelectBranchData(selectBranchDataValue, branchData);
  const roleAdmin = useSelector((state: RootState) => state?.auth?.data?.role?.role_type);

  const handleGetProductX = async (): Promise<void> =>
    handleGetProductByItemCode(
      itemCodeX.current.value,
      setGetDataProduct,
      setErrorMessageSearchProduct
    );

  const handleSubmitForm = (): void => {
    if (handleValidate()) {
      setOpenConfirmForm(true);
    }
  };

  const gatherFormData = () => ({
    promotionName: promotionName.current.value,
    promotionType: promotionType,
    buyProduct: dataProductBySelect?.map((item) => ({
      quantity: item?.quantity,
      uomQuantity: item?.price?.quantity,
      itemcode: item?.price?.itemcode,
    })),
    getPoint: parseInt(getPoint?.current.value) || 0,
    startDate: getDateTimeFromRef(startDate, selectedStartDateHour),
    endDate: getDateTimeFromRef(endDate, selectedEndDateHour),
    merchantIds: getBranchId,
    tagId: selectedTagPromotion,
    limitUse: parseInt(limitUse.current.value),
    limitUseOfCustomer: parseInt(limitUseOfCustomer.current.value),
    limitUserPerDay: parseInt(limitProPerDay.current.value) || 0,
    flashSale: isFlashSale,
    description: description.current?.value || '',
    ...(isFlashSale && {
      flashSaleStartDate: getDateTimeFromRef(flashSaleStartDate, selectedFlashSaleStartDateHour),
      flashSaleEndDate: getDateTimeFromRef(flashSaleEndDate, selectedFlashSaleEndDateHour),
    }),
  });

  const handleCreatePromotion = async () => {
    try {
      setLoadingButton(true);
      const formData = gatherFormData();
      await createPromotionService(formData);
      await onFetchData();
      onClose();
    } catch (error: any) {
      if (error.failedItems && error.failedItems.length > 0) {
        // Extract failed merchant messages
        const failedMerchantMessages = error.failedItems
          .map((item: any) => {
            // Find the matching merchant from branchData using the item's merchantId
            const matchingMerchant = branchData?.find((branch) => branch?.id === item.merchantId);
            // Use the merchant ID or a merchant name if available, fallback to item.merchantId if no match
            const merchantIdentifier = i18n.language === LA_ACCEPT_LANGUAGE ? matchingMerchant?.nameLa : matchingMerchant?.nameEn || matchingMerchant?.id || item.merchantId;
            return `${t('validatr_promotion.Sorry, this Item code')} : ${item.itemCode} ${t('validatr_promotion.in Merchant')}: ${merchantIdentifier} ${t('validatr_promotion.has an assigned promotion')}`;
          })
          .join('\n');

        setErrorMessage(failedMerchantMessages);
      } else {
        // Handle general errors
        setErrorMessage(error.message || t('validatr_promotion.Unexpected error occurred.'));
      }

      handleCloseConfirmFom();
    } finally {
      setLoadingButton(false);
    }
  };

  const handleValidate = () => {

    const setError = (message:string) => {
      setErrorMessage(t(message) || '');
      return false;
    };
  
    const validateDateRange = (start:any, end:any, startError:string, endError:string, rangeError:string) => {
      if (!start.isValid()) return setError(startError);
      if (!end.isValid()) return setError(endError);
      if (start.isAfter(end)) return setError(rangeError);
      return true;
    };
  
    const validateNumber = (value:string, errorMessage:string) => {
      if (!validateNumberInput(value, t(errorMessage))) return false;
      return true;
    };    
    
    // Validate required fields
    if (!selectedTagPromotion) return setError('validatr_promotion.Please select tag promotion');
    if (!promotionName?.current.value) return setError('validatr_promotion.Promotion name is required');
    if (!description.current.value) return setError('validatr_promotion.Description is required');
    if (selectBranchDataValue.length === 0) {setErrorMessage(t('validatr_promotion.Please select Branch') || ''); 
      return false;
    }

    // Validate flash sale dates
    if (isFlashSale) {
      const flashSaleStart = moment(new Date(flashSaleStartDate?.current.value));
      const flashSaleEnd = moment(new Date(flashSaleEndDate?.current.value));
  
      if (
        !validateDateRange(
          flashSaleStart, flashSaleEnd,
          'validatr_promotion.Flash sale start date is invalid',
          'validatr_promotion.Flash sale end date is invalid',
          'validatr_promotion.Flash sale start date must be before flash sale end date'
        )
      ) {
        return false;
      }
  
      if (!selectedFlashSaleStartDateHour) return setError('validatr_promotion.Please select FlashSale start Hour');
      if (!selectedFlashSaleEndDateHour) return setError('validatr_promotion.Please select FlashSale end Hour');
    }

    // Validate main date range
    const start = moment(new Date(startDate.current.value));
    const end = moment(new Date(endDate.current.value));
    if (
      !validateDateRange(
        start, end,
        'validatr_promotion.Start date is invalid',
        'validatr_promotion.End date is invalid',
        'validatr_promotion.Start date must be before end date'
      )
    ) {
      return false;
    }    

    if (!selectedStartDateHour) return setError('validatr_promotion.Please select start Hour');
    if (!selectedEndDateHour) return setError('validatr_promotion.Please select end Hour');
    if (!selectedProductValue) return setError('validatr_promotion.Please select product');
    if (dataProductBySelect.length === 0) {setError(t('validatr_promotion.Please select product'))
      return false;
    }

    // Validate number inputs
    if (!validateNumber(quantityBuyProduct.current?.value, 'validatr_promotion.buy product quantity must be a number')) return false;
    if (!validateNumber(limitUse.current?.value, 'validatr_promotion.Limit use must be a number')) return false;
    if (!validateNumber(limitUseOfCustomer.current?.value, 'validatr_promotion.Limit use of customer must be a number')) return false;
    if (!validateNumber(getPoint.current?.value, 'validatr_promotion.discount price must be a number')) return false;   
     

    // Cross-field numeric check
    const limitUseValue = parseInt(limitUse?.current?.value);
    const limitUseOfCustomerValue = parseInt(limitUseOfCustomer?.current?.value);
    const limitProPerDayValue = parseInt(limitProPerDay?.current?.value);
    
    if (limitUseValue < limitUseOfCustomerValue) {
      setErrorMessage(t('validatr_promotion.Limit use of customer must not be exceed limit of promotion') || '')
      return false;
    }

    if (selectedPromotionPerDay === 'per_day') {
      const isValid = validateNumberInput(limitProPerDay.current?.value, t('validatr_promotion.Limit use per day must be a number'));
      if (!isValid) {
        return;
      }
      if (limitUseValue < limitProPerDayValue) {
          setErrorMessage(t('validatr_promotion.Limit use must not be less than limit per day') || '');
          return false; 
      }
      if (limitUseOfCustomerValue < limitProPerDayValue) {
        setErrorMessage(t('validatr_promotion.limit Use Of Customer must not be less than limit per day') || '');
        return false; 
      }
    }    
    
    setErrorMessage('');
    return true;
  };

  const validateNumberInput = (value: string | undefined, errorMessage: string): boolean => {
    if (!value) {
      setErrorMessage(t('validatr_promotion.Please enter a number') || '');
      return false;
    }
    if (!/^\d+$/.test(value)) {
      setErrorMessage(errorMessage);
      return false;
    }
    return true;
  };

  const handleCloseConfirmFom = () => {setOpenConfirmForm(false)};
  const handleSelectPromotionPerDay = (event: ChangeEvent<HTMLInputElement>) => {setSelectedPromotionPerDay(event.target.value as PromotionTypeEnumPromotion)};
  const handleSelectTagPromotion = (event: SelectChangeEvent<string>) => {setSelectedTagPromotion(event.target.value)};

  const handleGetProductBySelect = () => {
    const quantity = Number(quantityBuyProduct.current?.value) || 1;
    const getProduct = getDataProduct?.find(
      (item: ProductMerchantModel) => item?.price?.quantity === selectedProductValue
    );

    if (getProduct) {
      setDataProductBySelect((prevDataProductBySelect) => {
        const existingProductIndex = prevDataProductBySelect.findIndex(
          (product) => product.id === getProduct.id
        );

        if (existingProductIndex !== -1) {
          // Update quantity if product already exists
          const updatedProducts = [...prevDataProductBySelect];
          updatedProducts[existingProductIndex] = {
            ...updatedProducts[existingProductIndex],
            quantity: quantity,
          };
          return updatedProducts;
        } else {
          // Add new product with quantity
          return [
            ...prevDataProductBySelect,
            {
              ...getProduct,
              quantity: quantity,
            },
          ];
        }
      });
    }
  };

  const handleDeleteProduct = (id: string) => {
    setDataProductBySelect((prevDataProductBySelect) =>
      prevDataProductBySelect.filter((product: ProductMerchantModel) => product?.id !== id)
    );
  };

  const handleSelectedBranch = (event: SelectChangeEvent<any>) => {
    const {target: { value }} = event;
    setSelectBranchDataValue(typeof value === 'string' ? value.split(',') : value);
  };

  const filterGetTagPromotionByType = tagPromotionData?.filter((item: TagPromotionModel) => item?.tagType === promotionType);

  useEffect(() => {
    if (selectedData) {
      promotionName.current.value = selectedData?.promotionName;
      limitProPerDay.current.value = selectedData?.limitUserPerDay?.toString();
      startDate.current.value = moment(selectedData?.startDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      endDate.current.value = moment(selectedData?.endDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      flashSaleStartDate.current.value = moment( selectedData?.flashSaleStartDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      flashSaleEndDate.current.value = moment( selectedData?.flashSaleEndDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      limitUse.current.value = selectedData?.limitUse.toString();
      limitUseOfCustomer.current.value = selectedData?.limitUseOfCustomer.toString();
      description.current.value = selectedData?.description;
      getPoint.current.value = selectedData?.getPoint?.toString();
      setSelectedProductValue(selectedData?.buyProduct[0]?.quantity);
      setSelectedTagPromotion(selectedData?.tagId);
      setIsFlashSale(selectedData?.flashSale);
      setSelectedStartDateHour(moment(selectedData?.startDate, 'MM/DD/YYYY,HH:mm:ss').format('HH'));
      setSelectedEndDateHour(moment(selectedData?.endDate, 'MM/DD/YYYY,HH:mm:ss').format('HH'));
      setSelectedFlashSaleStartDateHour( moment( selectedData?.flashSaleStartDate, 'MM/DD/YYYY,HH:mm:ss').format('HH'));
      setSelectedFlashSaleEndDateHour( moment( selectedData?.flashSaleEndDate, 'MM/DD/YYYY,HH:mm:ss').format('HH'));
      if (selectedData?.limitUserPerDay && selectedData?.limitUserPerDay >= 1) {
        setSelectedPromotionPerDay(PromotionTypeEnumPromotion.PER_DAY);
      } else {
        setSelectedPromotionPerDay(PromotionTypeEnumPromotion.PROMOTION);
      }
      setSelectBranchDataValue([
        i18n.language === LA_ACCEPT_LANGUAGE
          ? selectedData?.branch?.nameLa
          : i18n.language === EN_ACCEPT_LANGUAGE
          ? selectedData?.branch?.nameEn
          : selectedData?.branch?.nameLa,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData]);

  return {
    roleAdmin,
    isFlashSale,
    itemCodeX,
    branchData,
    promotionName,
    description,
    errorMessage,
    startDate,
    endDate,
    flashSaleStartDate,
    flashSaleEndDate,
    limitUse,
    limitUseOfCustomer,
    limitProPerDay,
    getDataProduct,
    loadingButton,
    openConfirmForm,
    getPoint,
    dataProductBySelect,
    quantityBuyProduct,
    errorMessageSearchProduct,
    filterGetTagPromotionByType,
    selectedProductValue,
    selectedPromotionPerDay,
    selectedTagPromotion,
    selectBranchDataValue,
    selectedStartDateHour,
    selectedEndDateHour,
    selectedFlashSaleStartDateHour,
    selectedFlashSaleEndDateHour,
    handleSelectedBranch,
    handleGetProductX,
    handleSelectPromotionPerDay,
    handleSelectTagPromotion,
    handleGetProductBySelect,
    handleDeleteProduct,
    handleCloseConfirmFom,
    handleSubmitForm,
    handleCreatePromotion,
    handleSelectFlashSale: (newValue: boolean) => setIsFlashSale(newValue),
    handleSelectedProduct: (event: SelectChangeEvent<any>) => setSelectedProductValue(event.target.value),
    handleSelectedStartDateHour: (event: React.ChangeEvent<HTMLInputElement>) => setSelectedStartDateHour(event.target.value),
    handleSelectedEndDateHour: (event: React.ChangeEvent<HTMLInputElement>) => setSelectedEndDateHour(event.target.value),
    handleSelectedFlashSaleStartDateHour: (event: React.ChangeEvent<HTMLInputElement>) => setSelectedFlashSaleStartDateHour(event.target.value),
    handleSelectedFlashSaleEndDateHour: (event: React.ChangeEvent<HTMLInputElement>) => setSelectedFlashSaleEndDateHour(event.target.value),
  };
};
export default useBuyXGetCSTPromotionController;
